// src/pages/PrivacyPolicyPage.jsx
import React from 'react';
import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';

import AnimatedSection, { fadeInUp } from '../components/AnimatedSection'; // Adjust path if needed
import ContactForm from '../components/ContactForm'; // Adjust path if needed

const AnimatedTitle = ({ title }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const wordVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: 'spring',
        stiffness: 100,
        damping: 12,
      },
    },
  };

  return (
    <motion.h2
      ref={ref}
      className="text-2xl md:text-3xl font-bold text-gray-900"
      variants={containerVariants}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      aria-label={title}
    >
      {title.split(" ").map((word, index) => (
        <motion.span
          key={index}
          variants={wordVariants}
          className="inline-block mr-[0.25em]"
        >
          {word}
        </motion.span>
      ))}
    </motion.h2>
  );
};

const PolicySection = ({ title, children }) => {
  return (
    <div className="md:grid md:grid-cols-3 md:gap-12 border-t border-gray-200 pt-8">
      <div className="md:col-span-1 mb-4 md:mb-0">
        <AnimatedTitle title={title} />
      </div>
      <div className="md:col-span-2 text-lg leading-relaxed text-gray-700 space-y-4 border-l border-gray-200 pl-8">
        {children}
      </div>
    </div>
  );
};

function PrivacyPolicyPage() {
  return (
    <>
      {/* Hero Section for the Page Title (Left-Aligned) */}
      <AnimatedSection
        animationVariants={fadeInUp}
        className="bg-black text-white pt-32 pb-16 md:pt-48 md:pb-24"
      >
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="text-left">
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold leading-tight">
              Privacy Policy
            </h1>
            <p className="mt-4 text-lg text-gray-300">
              Effective Date: September 15, 2024
            </p>
          </div>
        </div>
      </AnimatedSection>

      {/* Main Content Section for the Policy Text */}
      <AnimatedSection className="py-16 md:py-24 bg-gray-50">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="space-y-16">
            
            {/* Introduction */}
            <div className="text-lg leading-relaxed text-gray-700 max-w-4xl">
              <p>
                <a href="https://www.zenithblox.network" target="_blank" rel="noopener noreferrer" className="font-semibold text-purple-700 hover:text-purple-800 transition-colors">www.zenithblox.network</a> (the "Site") is owned and operated by ZenithBlox Inc. ZenithBlox Inc. can be contacted at: <a href="mailto:contact@zenithblox.network" className="font-semibold text-purple-700 hover:text-purple-800 transition-colors">contact@zenithblox.network</a>.
              </p>
            </div>

            <PolicySection title="Purpose">
              <p>The purpose of this Privacy Policy is to inform users of our Site of the following:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>The personal data we collect;</li>
                <li>The use of collected data;</li>
                <li>Who has access to the data collected;</li>
                <li>The rights of Site users; and</li>
                <li>The Site's cookie policy.</li>
              </ul>
              <p>This Privacy Policy applies in addition to the Terms and Conditions of our Site.</p>
            </PolicySection>

            <PolicySection title="Consent">
              <p>By using our Site, users agree that they consent to:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>The conditions set out in this Privacy Policy; and</li>
                <li>The collection, use, and retention of the data listed in this Privacy Policy.</li>
              </ul>
            </PolicySection>
            
            <PolicySection title="Personal Data We Collect">
                <p>We only collect data that helps us achieve the purposes set out in this Privacy Policy. We will not collect any additional data beyond what is listed below without notifying you first.</p>
                <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-2">Data Collected Automatically</h3>
                <p>When you visit and use our Site, we may automatically collect and store the following information:</p>
                <ul className="list-disc pl-6 space-y-2">
                    <li>IP address;</li>
                    <li>Location;</li>
                    <li>Hardware and software details;</li>
                    <li>Clicked links; and</li>
                    <li>Content viewed.</li>
                </ul>
                <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-2">Data Collected Non-Automatically</h3>
                <p>We may also collect the following data when you perform certain functions on our Site:</p>
                <ul className="list-disc pl-6 space-y-2">
                    <li>First and last name;</li>
                    <li>Email address; and</li>
                    <li>Phone number.</li>
                </ul>
                <p>This data may be collected via the "Contact Us" form.</p>
            </PolicySection>

            <PolicySection title="How We Use Personal Data">
              <p>Data collected on our Site will only be used for the purposes specified in this Privacy Policy or as indicated on the relevant pages of our Site. We will not use your data beyond what we disclose in this Privacy Policy.</p>
              <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-2">Automated Data Usage</h3>
              <p>The data we collect automatically is used for the following purposes:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>To provide, maintain, and improve our services;</li>
                <li>To communicate with you regarding your account, our services, and promotional offers; and</li>
                <li>To enforce our Terms of Service and comply with legal obligations.</li>
              </ul>
               <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-2">Non-Automated Data Usage</h3>
              <p>The data we collect when you perform certain functions may be used to communicate with you about our services and promotional offers.</p>
            </PolicySection>
            
            <PolicySection title="Who We Share Personal Data With">
              <h3 className="text-xl font-semibold text-gray-800 mb-2">Employees</h3>
              <p>We may disclose user data to any member of our organization who reasonably needs access to it to achieve the purposes set out in this Privacy Policy.</p>
              <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-2">Other Disclosures</h3>
              <p>We will not sell or share your data with other third parties, except in the following cases:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>If required by law;</li>
                <li>If required for any legal proceeding;</li>
                <li>To prove or protect our legal rights; and</li>
                <li>To buyers or potential buyers of this company in the event that we seek to sell the company.</li>
              </ul>
              <p>If you follow hyperlinks from our Site to another website, please note that we are not responsible for and have no control over their privacy policies and practices.</p>
            </PolicySection>
             
            <PolicySection title="Data Storage and Protection">
              <h3 className="text-xl font-semibold text-gray-800 mb-2">How Long We Store Personal Data</h3>
              <p>User data will be stored until the purpose for which the data was collected has been achieved. You will be notified if your data is kept for longer than this period.</p>
              <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-2">How We Protect Your Personal Data</h3>
              <p>We implement a variety of security measures to maintain the safety of your personal information. However, please be aware that no method of transmission over the Internet or method of electronic storage is 100% secure.</p>
              <p>While we take all reasonable precautions to ensure that user data is secure and that users are protected, there always remains a risk of harm. The Internet as a whole can be insecure at times, and therefore we are unable to guarantee the security of user data beyond what is reasonably practical.</p>
            </PolicySection>

            <PolicySection title="User Rights and Policies">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Children</h3>
                <p>We do not knowingly collect or use personal data from children under 13 years of age. If we learn that we have collected personal data from a child under 13, the personal data will be deleted as soon as possible. If a child under 13 has provided us with their personal data, their parent or guardian may contact our privacy officer.</p>
                <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-2">How to Access, Modify, or Delete Data</h3>
                <p>If you would like to know if we have collected your personal data, how we have used it, if we have disclosed it and to whom, or if you would like your data to be deleted or modified in any way, please contact our privacy officer here: <a href="mailto:contact@zenithblox.network" className="font-semibold text-purple-700 hover:text-purple-800 transition-colors">contact@zenithblox.network</a>.</p>
                <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-2">Do Not Track Notice</h3>
                <p>Do Not Track ("DNT") is a privacy preference that you can set in certain web browsers. We do not track users of our Site over time and across third-party websites and therefore do not respond to browser-initiated DNT signals.</p>
                 <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-2">How to Opt-Out</h3>
                <p>In addition to the methods described in the "How to Access, Modify, or Delete Data" section, you may opt-out of marketing emails by clicking the "unsubscribe" link at the bottom of any marketing email you receive from us.</p>
            </PolicySection>

            <PolicySection title="Cookie Policy">
              <p>A cookie is a small file stored on a user's hard drive by a website, which collects data relating to the user's Browse habits. You can choose to be notified each time a cookie is transmitted. You can also disable cookies entirely in your internet browser, but this may decrease the quality of your user experience.</p>
              <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-2">Functional Cookies</h3>
              <p>We use functional cookies to remember the selections you make on our Site so that your choices are saved for your next visits.</p>
            </PolicySection>
            
            <PolicySection title="Modifications">
              <p>This Privacy Policy may be amended from time to time to maintain compliance with the law and to reflect any changes to our data collection process. When we amend this Privacy Policy, we will update the "Effective Date" at the top of this policy. We recommend that our users periodically review our Privacy Policy to ensure that they are aware of any updates. If necessary, we may notify users by email of changes to this policy.</p>
            </PolicySection>
            
            <PolicySection title="Contact Information">
              <p>If you have any questions, concerns, or complaints, you can contact our privacy officer at: <a href="mailto:contact@zenithblox.network" className="font-semibold text-purple-700 hover:text-purple-800 transition-colors">contact@zenithblox.network</a>.</p>
            </PolicySection>

          </div>
        </div>
      </AnimatedSection>

      {/* Consistent Contact Form CTA at the bottom of the page */}
      <AnimatedSection triggerOnce={false} animationVariants={fadeInUp} threshold={0.05}>
        <ContactForm />
      </AnimatedSection>
    </>
  );
}

export default PrivacyPolicyPage;